/**
 * Hook for persisting evaluation state to session storage using Zion Cache
 */

import { useCallback, useMemo } from 'react'
import Cache from '@fs/zion-cache'
import type { NameEntry } from '../types'

const STORAGE_KEY = 'transliteration-eval-state'
const EXPIRATION_HOURS = 24

interface SavedState {
  uploadedData: NameEntry[]
  currentIndex: number
  evaluations: Record<string, NameEntry['candidates']>
  savedAt: string
}

export const useLocalStorage = (): {
  saveState: (
    uploadedData: NameEntry[],
    currentIndex: number,
    evaluations: Record<string, NameEntry['candidates']>
  ) => Promise<void>
  loadState: () => Promise<SavedState | null>
  clearState: () => Promise<void>
} => {
  // Create session storage cache instance
  const cache = useMemo(
    () =>
      new Cache({
        storeName: 'transliteration_eval',
      }),
    []
  )

  const clearState = useCallback(async () => {
    try {
      await cache.removeItem(STORAGE_KEY)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to clear session storage:', err)
    }
  }, [cache])

  const saveState = useCallback(
    async (uploadedData: NameEntry[], currentIndex: number, evaluations: Record<string, NameEntry['candidates']>) => {
      try {
        const state: SavedState = {
          uploadedData,
          currentIndex,
          evaluations,
          savedAt: new Date().toISOString(),
        }
        await cache.setItem(STORAGE_KEY, state)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to save state to session storage:', err)
      }
    },
    [cache]
  )

  const loadState = useCallback(async (): Promise<SavedState | null> => {
    try {
      const state = await cache.getItem<SavedState>(STORAGE_KEY)
      if (!state) return null

      const savedAt = new Date(state.savedAt)
      const now = new Date()
      const hoursSince = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60)

      // Expire after 24 hours
      if (hoursSince > EXPIRATION_HOURS) {
        await clearState()
        return null
      }

      return state
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load state from session storage:', err)
      return null
    }
  }, [cache, clearState])

  return { saveState, loadState, clearState }
}
